import * as Yup from 'yup';
import { useApiGet, useApiPost, useApiPut } from '../../../../services/tanstackApiService';
import type {
  Service,
  Option,
  InvoiceFormValues,
  InvoiceDetailsResponse,        // <-- import shared interface
  UnbilledInvoiceService,
  UnbilledInvoiceResponse,
  PackageCloserServicesResponse,
} from './newInvoice.types';
import { getRequest, postRequest } from '../../../../services/httpService';
import { useQuery } from '@tanstack/react-query';
import { PaymentApiPayload } from '../../collectPayment/collectPayment.types';
import { useClinic } from '../../../../components/ClinicProvider';


// ---------------------------------------------------------------------
// Initial values & validation schema (already used in NewInvoiceForm)
// ---------------------------------------------------------------------

export const initialInvoiceValues: InvoiceFormValues = {
  patientSource: '',
  tariff: '',
  serviceSearch: '',
  services: [],
  concessionReason: '',
  dueDate: '',
  termsAndConditions: '',
  legalMentions: '',
  remark: '',
  comments: '',
};

export const validationInvoiceSchema = Yup.object().shape({
  patientSource: Yup.string().required(),
  tariff: Yup.string().required(),
  serviceSearch: Yup.string(),
  services: Yup.array().of(
    Yup.object({
      provider: Yup.string()
        .nullable()
        .test(
          'provider-required-when-checked',
          'Provider is required',
          function (value) {
            const service = this.parent as { checked?: boolean };
            if (service?.checked) {
              return !!value;
            }
            return true;
          }
        ),
      charges: Yup.number().nullable(),
      concession: Yup.object({
        amount: Yup.number()
          .min(0, 'Concession amount cannot be negative')
          .test(
            'amount-lte-charges',
            'Concession amount cannot exceed charges',
            function (value) {
              // Access charges from the parent service object (2 levels up)
              const service = this.from?.[1]?.value as { charges?: number | null };
              const charges = service?.charges;
              
              // If no charges or charges is 0, concession must be 0
              if (charges === undefined || charges === null || charges === 0) {
                return value === undefined || value === null || value === 0;
              }
              if (value === undefined || value === null) return true;
              return Number(value) <= Number(charges);
            }
          ),
        percent: Yup.number()
          .min(0, 'Percent cannot be negative')
          .max(100, 'Percent cannot exceed 100')
          .test(
            'percent-when-no-charges',
            'Cannot add concession when charges is 0',
            function (value) {
              const service = this.from?.[1]?.value as { charges?: number | null };
              const charges = service?.charges;
              
              // If charges is 0, percent must also be 0
              if (charges === 0) {
                return value === undefined || value === null || value === 0;
              }
              return true;
            }
          ),
      }),
      tax: Yup.object({
        amount: Yup.number()
          .min(0, 'Tax amount cannot be negative')
          .test(
            'tax-amount-lte-charges',
            'Tax amount cannot exceed charges',
            function (value) {
              const service = this.from?.[1]?.value as { charges?: number | null };
              const charges = service?.charges;
              
              // If no charges or charges is 0, tax must be 0
              if (charges === undefined || charges === null || charges === 0) {
                return value === undefined || value === null || value === 0;
              }
              if (value === undefined || value === null) return true;
              return Number(value) <= Number(charges);
            }
          ),
        percent: Yup.number()
          .min(0, 'Percent cannot be negative')
          .max(100, 'Percent cannot exceed 100')
          .test(
            'percent-when-no-charges',
            'Cannot add tax when charges is 0',
            function (value) {
              const service = this.from?.[1]?.value as { charges?: number | null };
              const charges = service?.charges;
              
              // If charges is 0, percent must also be 0
              if (charges === 0) {
                return value === undefined || value === null || value === 0;
              }
              return true;
            }
          ),
      }),
    })
  ),
  concessionReason: Yup.string().test(
    'required-if-concession',
    'Concession reason is required when concession is applied',
    function (value) {
      const services = this.parent.services as Service[];
      
      // Only require concession reason if a NON-PACKAGE service has concession applied
      // Package concessions are pre-defined and don't need a reason
      const hasNonPackageConcession = services?.some((service) => {
        // Use is_from_package flag or package_id to detect package services
        // Don't use !!service.package as it's a display string that can be populated independently
        const isFromPackage = service.is_from_package === true || service.package_id != null;
        if (isFromPackage) return false; // Skip package services
        
        const concessionAmount = service.concession?.amount ?? 0;
        const concessionPercent = service.concession?.percent ?? 0;
        return concessionAmount > 0 || concessionPercent > 0;
      });
      
      // If there's a concession on non-package service, the reason is required
      if (hasNonPackageConcession) {
        return !!value && value.trim().length > 0;
      }
      
      // No non-package concession, so reason is not required
      return true;
    }
  ),
  dueDate: Yup.string().required(),
  termsAndConditions: Yup.string(),
  legalMentions: Yup.string(),
  remark: Yup.string(),
  comments: Yup.string(),
});

// ---------------------------------------------------------------------
// API response interfaces 
// ---------------------------------------------------------------------

// NOTE: UnbilledInvoiceService / UnbilledInvoiceResponse
// now come from newInvoice.types.ts, so the local definitions here
// can be removed if you have moved them; keep only what is not moved.
export interface BillingConfigOptionItem {
  id: number;
  name: string;
  code: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  translation?: any;
}

// specific type for tariff_group items
export interface TariffGroupItem {
  id: number;
  tariff_id: number;
  tariff_name: string;
  tariff_code: string;
  patient_source_id: number;
  patient_source_name: string;
  patient_source_code: string;
}

// NEW: type for patient source with embedded tariffs, e.g. Walk-In / Insurance
export interface PatientSourceWithTariffs {
  label: string;        // "Walk-In"
  value: string;        // "Walk-In"
  tariffs: {
    id: number;
    tariff_id: number;
    tariff_name: string; // "International"
    tariff_code: string; // "INTERNATIONAL"
  }[];
}

export interface BillingConfigOptionsResponse {
  CONCESSION_REASON: BillingConfigOptionItem[];
  SERVICE_SOURCE_TYPE: BillingConfigOptionItem[];
  PACKAGE_PROCEDURE_MASTER: BillingConfigOptionItem[];
  PAYMENT_MODE?: BillingConfigOptionItem[];
  CANCEL_REASON?: BillingConfigOptionItem[];
  UPI_PROVIDER?: BillingConfigOptionItem[];
  item_group: BillingConfigOptionItem[];
  item_category: BillingConfigOptionItem[];
  uom: BillingConfigOptionItem[];
  tariff_group: TariffGroupItem[];
  tariff_source_group: PatientSourceWithTariffs[];
}

// ---------------------------------------------------------------------
// Clinician types (for provider dropdown)
// ---------------------------------------------------------------------

export type Role = 'DOCTOR' | 'ANESTHETIST' | 'EMBRYOLOGIST' | 'NURSE';

export interface Clinician {
  id: number;
  first_name: string;
  last_name: string;

  // optional fields from /users response
  email?: string;
  username?: string;
  role?: string;
  departments?: Array<{
    id: number;
    name: string;
    clinic__id: number;
    clinic__name: string;
    name_label: string;
    tenant__name: string;
    is_clinical: boolean;
  }>;
  appointment_type?: string | null;
  is_active?: boolean;
  user_type?: string | null;
  language?: number;
  language_code?: string;
  language_name?: string;
  is_locked?: boolean;
  gender__name?: string | null;
  gender__name_label?: string | null;
  role_label?: string;
}

// ---------------------------------------------------------------------
// Route-specific constants
// ---------------------------------------------------------------------

export const DEFAULT_APPOINTMENT_ID = '120532';
export const DEFAULT_PATIENT_ID = 1711;

// ---------------------------------------------------------------------
// TanStack Query wrappers
// ---------------------------------------------------------------------

export function useUnbilledInvoiceServices(appointmentId: string | number, patientId?: string | number) {
  return useApiGet<UnbilledInvoiceResponse>(
    ['invoices', 'unbilled', appointmentId, 'services', patientId],
    {
      path: `invoices/unbilled/${appointmentId}/services/`,
      query: patientId ? { patient_id: patientId } : undefined,
    },
    {
      enabled: !!appointmentId,
    }
  );
}

/**
 * Fetch unbilled services for a patient (not appointment-specific)
 * GET /invoices/unbilled/services?patient_id={patientId}
 */
export function useUnbilledServicesByPatient(patientId?: string | number) {
  return useApiGet<UnbilledInvoiceResponse>(
    ['invoices', 'unbilled', 'services', 'patient', patientId],
    {
      path: 'invoices/unbilled/services/',
      query: { patient_id: patientId },
    },
    {
      enabled: !!patientId,
    }
  );
}

export function useBillingConfigOptions(patientId?: string | number) {
  return useApiGet<BillingConfigOptionsResponse>(
    ['billing', 'billing-config-options', patientId],
    {
      path: 'billing/billing-config-options/',
      query: { patient_id: patientId },
    },
    {
      enabled: !!patientId,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: 'always', // Refetch when navigating back to screen
      refetchOnReconnect: false,
    }
  );
}

// Use shared InvoiceDetailsResponse from types file
export function useInvoiceDetails(invoiceId?: string | number) {
  return useApiGet<InvoiceDetailsResponse>(
    ['invoices', 'details', invoiceId],
    {
      path: `invoices/${invoiceId}/`,
    },
    {
      enabled: !!invoiceId,
    }
  );
}

// Fetch package closer services for close package flow
export function usePackageCloserServices(packageId?: number | null) {
  return useApiGet<PackageCloserServicesResponse>(
    ['invoices', 'package-closer-services', packageId],
    {
      path: 'invoices/package-closer-services/',
      query: { package_id: packageId },
    },
    {
      enabled: !!packageId,
    }
  );
}

// ---------------------------------------------------------------------
// Mapping helpers
// ---------------------------------------------------------------------

export function mapUnbilledServicesToInvoiceServices(
  apiServices: UnbilledInvoiceService[]
): Service[] {
  return apiServices.map((item) => {
    const pkgRate = item.package_service_rates?.[0];
    // Use discount_amount/discount_percent from package_service_rates if available
    // to avoid rounding differences when recalculating from percent
    const discountAmount =
      typeof pkgRate?.discount_amount === 'number'
        ? pkgRate.discount_amount
        : item.concession_amount ?? 0;
    const discountPercent =
      typeof pkgRate?.discount_percent === 'number'
        ? pkgRate.discount_percent
        : item.concession_percentage ?? item.discount_percent ?? 0;

    return {
      date: item.date,
      code: item.billing_source_code,
      service: item.billing_source_name,
      // Preserve billing source fields for API submission
      billing_source_type: item.billing_source_type,
      billing_source_id: item.billing_source_id,
      billing_source_code: item.billing_source_code,
      billing_source_name: item.billing_source_name,
      package_id: item.package_id ?? undefined,
      package: item.package_name ?? '',
      // provider id should be stored as string so it matches Select option values
      provider: item.provider != null ? String(item.provider) : item.provider_id != null ? String(item.provider_id) : '',
      charges: item.charges,
      concession: {
        amount: discountAmount,
        percent: discountPercent,
      },
      tax: {
        amount: item.tax_amount ?? 0,
        percent: item.tax_percentage ?? 0,
      },
      net: item.net_amount,
      checked: true,
      isFromSearch: false,
      // optional: keep original discount for debugging / reuse
      initialDiscountPercent: discountPercent,
      is_from_package: item.is_from_package,
      is_from_consumption: item.is_from_consumption ?? false,
      package_consumption_id: item.package_consumption_id,
      package_service_rates: item.package_service_rates,
      prescribe_from_doctor: item.prescribe_from_doctor ?? false,
    };
  });
}

// Map /invoices/:id services -> internal `Service` without redefining Service shape
export function mapInvoiceServicesToInvoiceServices(
  apiServices: InvoiceDetailsResponse['services']
): Service[] {
  return apiServices.map((item) => {
    const charges = Number(item.charges) || 0;
    const concession = Number(item.concession_amount) || 0;
    const tax = Number(item.tax_amount) || 0;
    const netRaw = Number(item.net_amount);
    const net = Number.isNaN(netRaw) ? charges - concession + tax : netRaw;

    const svc: Service = {
      date: item.date,
      code: item.billing_source_code,
      service: item.billing_source_name,
      package: item.package_name ?? '',
      // Prefer display name when available; fallback to id string
      provider: item.provider != null ? String(item.provider) : '',
      charges,
      concession: {
        amount: concession,
        percent: Number(item.concession_percentage) || 0,
      },
      tax: {
        amount: tax,
        percent: Number(item.tax_percentage) || 0,
      },
      net,
      checked: true,
      isFromSearch: false,
      package_id: item.package_id ?? undefined,
      billing_source_type: item.billing_source_type,
      billing_source_id: item.billing_source_id,
      billing_source_code: item.billing_source_code,
      billing_source_name: item.billing_source_name,
      is_from_package: item.is_from_package,
      package_consumption_id: item.package_consumption_id,
      // package_service_rates not present in this API, leave undefined
    };

    return svc;
  });
}

export function mapConcessionReasonsToOptions(
  items: BillingConfigOptionItem[]
): Option[] {
  return items.map((item) => ({
    label: item.name,
    value: item.code, // or String(item.id) if required
  }));
}



// ----------------------
// Active Packages Types
// ----------------------

export interface ActivePackageItem {
  id: number;
  name: string;
  package_name: string;
  code: string;
  start_date?: string | null;
  end_date?: string | null;
  status?: string | null;
  [key: string]: unknown;
  package_amount: number;
  package_net_amount: number;
  package_concession_amount: number;
  total_service_concession_amount: number;
  package_advance_collected: number;
  advance_collected: number;
  package_used_amount: number;
  package_balance_amount: number;
  service_remaining_amount: number;
  service_amount: number;
  pharmacy_remaining_amount: number;
  pharmacy_amount: number;
  pharmacy_component_amount: number;
  pharmacy_component_percent: number;
  collected_pharmacy_advance_amount: number;
  remaining_pharmacy_advance_amount: number;
  // Donor-linked package fields (present when fetched via donor_id)
  patient_id?: number;
  patient_name?: string;
  patient_mrn?: string;
}

export type ActivePackagesResponse = ActivePackageItem[];

export function fetchActivePackages(
  patientId?: number | string, clinicId?: number | string,
  donorId?: number | string,
): Promise<ActivePackagesResponse> {
  const clinicParam = clinicId ? `&clinic=${clinicId}` : '';
  const param = donorId
    ? `donor_id=${donorId}`
    : `patient_id=${patientId}`;
  return getRequest(`invoices/active-packages/?${param}${clinicParam}`);
}

export function useActivePackages(
  patientId?: number | string,
  donorId?: number | string,
) {
  const { ClinicId } = useClinic();
  const enabled = !!(patientId || donorId);

  return useQuery<ActivePackagesResponse>({
    queryKey: ['invoices', 'active-packages', patientId, ClinicId, donorId],
    enabled,
    staleTime: 30_000,
    gcTime: 300_000,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    // Don't retry on 404 - it means no packages exist for this patient
    retry: (failureCount, error) => {
      // Don't retry on 404 (not found) or 400 (bad request)
      const status = (error as { status?: number })?.status;
      if (status === 404 || status === 400) {
        return false;
      }
      // Default: retry once for other errors
      return failureCount < 1;
    },
    queryFn: () => fetchActivePackages(patientId, ClinicId, donorId),
  });
}

// ---------------------------------------------------------------------
// Package consumption details (for modal)
// ---------------------------------------------------------------------

export interface PackageConsumptionDetailItem {
  package_id: number;
  service_id: number;
  service_name: string;
  procedure_name: string;
  gender: string;
  discount_percent: number;
  unit_rate: number;
  quantity: number;
  availed: number;
  pending: number;
  unlimited_quantity: boolean;
}

export interface PackageConsumptionDetailsResponse {
  package_id: string;           // display name
  package_net_amount: number;
  service_amount: number;
  pharmacy_amount: number;
  service_net_amount: number;
  used_amount: number;
  balance_amount: number;
  consumption_details: PackageConsumptionDetailItem[];
}

// Helper to fetch details
export function fetchPackageConsumptionDetails(
  packageId: number | string,
  billingSourceIds?: number[],
  options?: { pharmacyItems?: boolean; deptId?: number | null }
): Promise<PackageConsumptionDetailsResponse> {
  const billingSourceParams = billingSourceIds && billingSourceIds.length > 0
    ? `&billing_source_ids=${billingSourceIds.join(',')}`
    : '';
  const pharmacyParam = options?.pharmacyItems ? '&pharmacy_items=true' : '';
  const deptParam = options?.deptId ? `&dept_id=${options.deptId}` : '';
  return getRequest(
    `invoices/packages-consumption-details/?package_id=${packageId}${billingSourceParams}${pharmacyParam}${deptParam}`
  );
}

export function usePackageConsumptionDetails(packageId?: number | string) {
  const enabled = !!packageId;

  return useQuery<PackageConsumptionDetailsResponse>({
    queryKey: ['invoices', 'packages-consumption-details', packageId],
    enabled,
    queryFn: () => fetchPackageConsumptionDetails(packageId as number | string),
  });
}

// ---------------------------------------------------------------------
// Create / Update invoice
// ---------------------------------------------------------------------

// payload/response types for creating invoice (optional but useful)
export type CreateInvoicePayload = Record<string, unknown>;

// Small helper that hides the endpoint path
export function buildCreateInvoiceParams(body: CreateInvoicePayload) {
  return {
    path: 'invoices/',  // <-- endpoint only lives here
    body,
  };
}

// Hook to get the mutation instance
export function useCreateInvoice() {
  return useApiPost<unknown, CreateInvoicePayload>();
}

// Payload type for updating an invoice (same shape as backend expects)
export type UpdateInvoicePayload = Record<string, unknown>;

// Helper for PUT /invoices/:id/
export function buildUpdateInvoiceParams(id: number | string, body: UpdateInvoicePayload) {
  // use the same shape as generic PUT hook (path + body)
  return {
    path: `invoices/${id}/`,
    body,
  };
}

// Hook to update an existing invoice by id
export function useUpdateInvoice() {
  // useApiPut is the dedicated PUT wrapper; import it alongside useApiPost
  // from '../../../../services/tanstackApiService' in the imports above.
  // For brevity we only show the changed line here:
  return useApiPut<unknown, UpdateInvoicePayload>();
}

// ---------------------------------------------------------------------
// Clinician list (TanStack Query)
// ---------------------------------------------------------------------

export interface UsersApiResponse {
  meta: {
    limit: number;
    next: string | null;
    offset: number;
    previous: string | null;
    total_count: number;
  };
  objects: Clinician[]; // we only care about id/first_name/last_name here
}

export function useCliniciansByRole(
  clinicId: number | string | undefined,
  role: Role,
  enabled = true
) {
  return useQuery<UsersApiResponse>({
    queryKey: ['clinicians', clinicId, role],
    enabled: !!clinicId && enabled,
    queryFn: () =>
      getRequest(
        `users/?clinic=${clinicId}&role=${role}`
      ) as Promise<UsersApiResponse>,
  });
}

// Helper to build the API parameters for creating a payment
export function buildCreatePaymentParams(body: PaymentApiPayload) {
  return {
    path: 'payments/', // api endpoint for payments
    body,
  };
}

// Hook to POST and create a payment
export function useCreatePayment() {
  return useApiPost<unknown, PaymentApiPayload>();
}

// ---------------------------------------------------------------------
// Reusable helpers for Invoice Details + convenience hook
// ---------------------------------------------------------------------

// Build a stable query key for invoice details
export const invoiceDetailsKey = (id: number | string) =>
  (['invoices', 'details', id] as const);

// ---------------------------------------------------------------------
// Linked Recipients for Donor Billing
// ---------------------------------------------------------------------

export interface LinkedRecipient {
  id: number;
  name: string;
  mrn?: string;
  mobile_number?: string;
  gender?: string;
  patient_type?: string;
}

interface LinkedRecipientsResponse {
  success: boolean;
  donor_patient_id: number;
  donor_name: string;
  recipient_count: number;
  recipients: LinkedRecipient[];
}

export function fetchLinkedRecipients(donorPatientId: number | string): Promise<LinkedRecipientsResponse> {
  return getRequest(`patients/${donorPatientId}/linked-recipients/`);
}

// Mark settlement source (donor / recipient) on an invoice
export function markSettlementSource(
  invoiceId: number | string,
  paymentSource: 'donor' | 'recipient'
): Promise<{ success: boolean; invoice_id: number; payment_source: string; status: string }> {
  return postRequest(`invoices/${invoiceId}/mark-settlement-source/`, {
    payment_source: paymentSource,
  });
}

// Assign a recipient to a donor invoice
export function assignToRecipient(
  invoiceId: number | string,
  recipientPatientId: number
): Promise<{ success: boolean; invoice_id: number; recipient_patient_id: number; status: string }> {
  return postRequest(`invoices/${invoiceId}/assign-to-recipient/`, {
    recipient_patient_id: recipientPatientId,
  });
}

// De-link a recipient from a donor invoice (reason required)
export function delinkRecipient(
  invoiceId: number | string,
  reason: string
): Promise<{ success: boolean; invoice_id: number; delinked_recipient_id: number; status: string }> {
  return postRequest(`invoices/${invoiceId}/delink-recipient/`, { reason });
}

// --- Transfer Logs ---

export interface TransferLogEntry {
  id: number;
  action: string;
  recipient_patient_id: number | null;
  recipient_name: string | null;
  recipient_mrn: string | null;
  reason: string | null;
  created_on: string | null;
  created_by: string | null;
}

interface TransferLogsResponse {
  success: boolean;
  invoice_id: number;
  transfer_logs: TransferLogEntry[];
}

export function fetchTransferLogs(
  invoiceId: number | string
): Promise<TransferLogsResponse> {
  return getRequest(`invoices/${invoiceId}/transfer-logs/`);
}

// Build the REST path for invoice details
export const invoiceDetailsPath = (id: number | string) => `invoices/${id}/`;

// --- Unsettled Donor Invoices ---
export interface UnsettledDonorInvoice {
  id: number;
  bill_number: string | null;
  bill_type: string | null;
  total_amount: string | number;
  net_amount: string | number;
  paid_amount: string | number;
  balance_amount: string | number;
  concession_amount: string | number;
  due_date: string | null;
  remark: string | null;
  provider: number | null;
  provider_name_label: string | null;
  visit_date: string;
  donor_patient_id: number;
  donor_name: string | null;
  donor_mrn: string | null;
}

export interface UnsettledDonorInvoicesResponse {
  success: boolean;
  donor_patient_ids: number[];
  unsettled_invoices: UnsettledDonorInvoice[];
}

export function fetchUnsettledDonorInvoices(
  donorPatientIds: number[]
): Promise<UnsettledDonorInvoicesResponse> {
  const ids = [...donorPatientIds].join(',');
  return getRequest(`invoices/unsettled-donor-invoices/?donor_patient_id=${encodeURIComponent(ids)}`);
}

export function useUnsettledDonorInvoices(
  donorPatientIds: number[],
  options?: {
    /** Data considered fresh for this duration (ms). Default 24 hours. */
    staleTime?: number;
    /** Keep unused data in cache for this duration (ms). Default 24 hours. */
    gcTime?: number;
    /** Refetch when component mounts. Default false. Set 'always' to fetch on every render. */
    refetchOnMount?: boolean | 'always';
  }
) {
  const ONE_DAY = 24 * 60 * 60 * 1000;

  return useQuery<UnsettledDonorInvoicesResponse>({
    queryKey: ['invoices', 'unsettled-donor-invoices', ...donorPatientIds],
    queryFn: () => fetchUnsettledDonorInvoices(donorPatientIds),
    enabled: donorPatientIds.length > 0,
    staleTime: options?.staleTime ?? ONE_DAY,
    gcTime: options?.gcTime ?? ONE_DAY,
    refetchOnWindowFocus: false,
    refetchOnMount: options?.refetchOnMount ?? false,
    refetchOnReconnect: false,
  });
}

// --- Bulk Assign Donor Invoices to Recipient ---
export interface BulkAssignDonorInvoicesPayload {
  recipient_patient_id: number;
  donor_invoices: {
    donor_patient_id: number;
    invoice_ids: number[];
  }[];
}

export function bulkAssignDonorInvoices(
  payload: BulkAssignDonorInvoicesPayload
) {
  return postRequest('invoices/bulk-assign-recipient/', payload);
}

// Convenience hook specifically for /api/invoices/1
// Useful for testing or quick wiring before dynamic ids are available.
export function useInvoiceOne() {
  return useApiGet<InvoiceDetailsResponse>(
    invoiceDetailsKey(1),
    { path: invoiceDetailsPath(1) },
    { enabled: true }
  );
}

// ---------------------------------------------------------------------
// Validate Package Advance API
// ---------------------------------------------------------------------

export interface PackageAdvanceItem {
  package_id: number;
  amount: number;
}

export interface ValidatePackageAdvanceRequest {
  packages: PackageAdvanceItem[];
}

export interface CreditTransaction {
  credit_transaction_id: number;
  transaction_date: string;
  advance_against: 'PAYMENT' | 'PACKAGE_SERVICE' | string;
  package_name: string;
  total_amount: number;
  paid_amount: number;
  used_amount: number;
  balance_amount: number;
  refund_amount: number;
  hold_amount: number;
}

export interface PackageValidationResult {
  package_id: number;
  balance_amount: number;
  required_amount: number;
  valid: boolean;
  credit_transactions: CreditTransaction[];
}

export interface ValidatePackageAdvanceResponse {
  success: boolean;
  overall_valid: boolean;
  packages: PackageValidationResult[];
  message?: string;
}

// Hook to validate package advance before payment
export function useValidatePackageAdvance() {
  return useApiPost<ValidatePackageAdvanceResponse, ValidatePackageAdvanceRequest>();
}

// Build parameters for validate-package-advance API call
export function buildValidatePackageAdvanceParams(packages: PackageAdvanceItem[]) {
  return {
    path: 'billing/validate-package-advance/',
    body: { packages },
  };
}
